import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { useMSALAuth } from '../../contexts/MSALAuthContext';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * Guard component that checks if LSEG API key is configured.
 * If not configured, redirects to /onboarding before rendering any children.
 */
export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const { isAuthenticated, isAuthReady, user } = useMSALAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Start with checking = true to block rendering on first render
  // Only allow rendering on onboarding page immediately
  const [isCheckingConfig, setIsCheckingConfig] = useState(location.pathname !== '/onboarding');
  const [canRender, setCanRender] = useState(location.pathname === '/onboarding');

  useEffect(() => {
    const checkApiKeyConfig = async () => {
      // Skip check if not authenticated
      if (!isAuthenticated || !isAuthReady || !user) {
        setIsCheckingConfig(false);
        setCanRender(true);
        return;
      }

      // Skip check if already on onboarding page
      if (location.pathname === '/onboarding') {
        setIsCheckingConfig(false);
        setCanRender(true);
        return;
      }

      try {
        const lsegConfig = await apiClient.get('/lseg/config');
        const configData: any = lsegConfig.data || lsegConfig;
        
        // Check if API key is missing or empty - redirect to onboarding
        if (!configData.apiKey || configData.apiKey.trim() === '') {
          console.log('No LSEG API key configured, redirecting to onboarding...');
          navigate('/onboarding', { replace: true });
          // Don't set canRender to true - let the redirect happen
          setIsCheckingConfig(false);
          return;
        }
        
        // Config is valid, allow rendering
        setIsCheckingConfig(false);
        setCanRender(true);
      } catch (error) {
        console.error('Failed to check LSEG config:', error);
        // On error, allow rendering - don't block the entire app
        setIsCheckingConfig(false);
        setCanRender(true);
      }
    };

    checkApiKeyConfig();
  }, [isAuthenticated, isAuthReady, user, navigate, location.pathname]);

  // Show nothing while checking
  if (isCheckingConfig) {
    return null;
  }

  // Only render children if check passed
  if (!canRender) {
    return null;
  }

  return (
    <>
      <span hidden data-testid="auth-onboarding-guard-marker" />
      {children}
    </>
  );
};
